import { AutocompleteMultiple, ButtonInputWithPopover } from "@msys/ui";
import {
  Box,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  Typography,
} from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { RestrictedByCapabilityWithDebug } from "../../../auth/RestrictedByCapability.js";
import { FilterProps } from "../../../commons/filters/index.js";
import { useTeamsFilter_TeamsQuery } from "./TeamsFilter.generated.js";

const UNASSIGNED_KEY = "__unassigned__";

export type TeamFilterValue = Array<string | null>;

export function TeamsFilterButton(props: FilterProps<TeamFilterValue>) {
  return (
    <RestrictedByCapabilityWithDebug capability="TEAMS">
      <TeamsFilterButtonInner {...props} />
    </RestrictedByCapabilityWithDebug>
  );
}

export function TeamsFilter(props: FilterProps<TeamFilterValue>) {
  return (
    <RestrictedByCapabilityWithDebug capability="TEAMS">
      <TeamsFilterMultiSelect {...props} />
    </RestrictedByCapabilityWithDebug>
  );
}

function TeamsFilterButtonInner({
  value,
  setValue,
}: FilterProps<TeamFilterValue>) {
  const { t } = useTranslate("Projects");
  return (
    <ButtonInputWithPopover
      label={t("Team")}
      counter={value && value.length > 0 ? value.length : undefined}
      content={<TeamsFilterForm value={value} setValue={setValue} />}
      popoverWidth="md"
    />
  );
}

function TeamsFilterForm({ value, setValue }: FilterProps<TeamFilterValue>) {
  const { t } = useTranslate("Projects");
  const { data, loading } = useTeamsFilter_TeamsQuery({
    variables: { limit: 100 },
  });

  const teams =
    data?.teams.__typename === "TeamConnection"
      ? data.teams.edges.map(e => e.node)
      : [];

  const selected = new Set(value ?? []);

  const toggle = (id: string | null, checked: boolean) => {
    const next = new Set(selected);
    if (checked) {
      next.add(id);
    } else {
      next.delete(id);
    }
    setValue(Array.from(next));
  };

  if (!loading && teams.length === 0) {
    return (
      <Box p={2}>
        <Typography variant="body2">{t("No teams available")}</Typography>
      </Box>
    );
  }

  return (
    <Box p={1}>
      <FormControl component="fieldset" variant="standard">
        <FormGroup>
          {teams.map(team => (
            <FormControlLabel
              key={team.id}
              sx={{ ml: 0, mr: 0 }}
              control={
                <Checkbox
                  size="medium"
                  checked={selected.has(team.id)}
                  onChange={(_event, checked) => toggle(team.id, checked)}
                />
              }
              label={<Typography variant="body1">{team.name}</Typography>}
            />
          ))}
          <Divider sx={{ my: 0.5 }} />
          <FormControlLabel
            sx={{ ml: 0, mr: 0 }}
            control={
              <Checkbox
                size="medium"
                checked={selected.has(null)}
                onChange={(_event, checked) => toggle(null, checked)}
              />
            }
            label={<Typography variant="body1">{t("Unassigned")}</Typography>}
          />
        </FormGroup>
      </FormControl>
    </Box>
  );
}

type TeamFilterOption = {
  value: string;
  label: string;
  isUnassigned: boolean;
};

function TeamsFilterMultiSelect({
  value,
  setValue,
}: FilterProps<TeamFilterValue>) {
  const { t } = useTranslate("Projects");
  const { data } = useTeamsFilter_TeamsQuery({
    variables: { limit: 100 },
  });

  const teams =
    data?.teams.__typename === "TeamConnection"
      ? data.teams.edges.map(e => e.node)
      : [];

  const options: TeamFilterOption[] = [
    ...teams.map(team => ({
      value: team.id,
      label: team.name,
      isUnassigned: false,
    })),
    { value: UNASSIGNED_KEY, label: t("Unassigned"), isUnassigned: true },
  ];

  const selectedOptions: TeamFilterOption[] = (value ?? []).map(v =>
    v === null
      ? { value: UNASSIGNED_KEY, label: t("Unassigned"), isUnassigned: true }
      : {
          value: v,
          label: teams.find(team => team.id === v)?.name ?? v,
          isUnassigned: false,
        }
  );

  return (
    <AutocompleteMultiple
      inputLabel={t("Team")}
      options={options}
      value={selectedOptions}
      getOptionLabel={option => option.label}
      getOptionKey={option => option.value}
      isOptionEqualToValue={(option, selected) =>
        option.value === selected.value
      }
      onChange={newOptions => {
        setValue(
          newOptions.map(option => (option.isUnassigned ? null : option.value))
        );
      }}
    />
  );
}
